import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, RefreshCw } from "lucide-react";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/ui/skeleton";
import { djs } from "@/lib/djs";
import type { TriageGuidance } from "@/types/triage-guidance";

import { GuidanceFeedbackEntry } from "./guidance-feedback";
import { GuidanceProposalDialog } from "./guidance-proposal-dialog";
import { GuidanceReviewDialog } from "./guidance-review-dialog";
import { SectionCard } from "./section-card";

const labels = { proposed: "aguardando revisão", approved: "em uso", rejected: "fora de uso" };

export function GuidanceSection() {
	const { data, isPending, isError, isFetching, refetch } = useQuery(
		orpc.system.guidance.queryOptions(),
	);
	const [proposing, setProposing] = useState(false);

	return (
		<SectionCard
			title="orientações da triagem"
			dataComponent="system-guidance"
			footer={
				<Button type="button" size="sm" variant="outline" onClick={() => setProposing(true)}>
					<Plus /> propor orientação
				</Button>
			}
		>
			<p className="mb-4 text-sm text-muted-foreground">
				aprendizados de decisões da equipe. cada orientação precisa de revisão antes de entrar nas
				próximas análises.
			</p>
			{isPending && <SkeletonRows rows={3} />}
			{isError && (
				<div role="alert">
					<p className="text-sm text-destructive">não foi possível carregar as orientações.</p>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						disabled={isFetching}
						onClick={() => refetch()}
					>
						<RefreshCw /> tentar de novo
					</Button>
				</div>
			)}
			{data?.length === 0 && (
				<p className="text-sm text-muted-foreground">nenhuma orientação proposta ainda.</p>
			)}
			{data !== undefined && (
				<ul className="flex flex-col gap-3">
					{data.map((guidance) => (
						<li key={guidance.id}>
							<GuidanceCard guidance={guidance} />
						</li>
					))}
				</ul>
			)}
			{proposing && <GuidanceProposalDialog onClose={() => setProposing(false)} />}
		</SectionCard>
	);
}

function GuidanceCard({ guidance }: { guidance: TriageGuidance }) {
	const [reviewing, setReviewing] = useState<"approved" | "rejected" | null>(null);
	const rejectLabel = { proposed: "rejeitar", approved: "revogar", rejected: "rejeitar" }[
		guidance.status
	];

	return (
		<article
			data-slot="guidance-card"
			data-guidance-id={guidance.id}
			className="rounded-lg border border-border p-4"
		>
			<div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
				<span className="font-medium text-primary">{labels[guidance.status]}</span>
				<span>
					proposta por {guidance.created_by}, {djs(guidance.created_at).format("DD/MM/YYYY")}
				</span>
			</div>
			<p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
				{guidance.content}
			</p>
			<details className="mt-3 text-sm">
				<summary className="cursor-pointer font-medium text-primary">decisões e revisões</summary>
				<ul className="mt-3 flex flex-col gap-4">
					{guidance.feedback.map((feedback) => (
						<li key={feedback.id}>
							<GuidanceFeedbackEntry feedback={feedback} />
						</li>
					))}
				</ul>
				{guidance.reviews.length > 0 && (
					<ol className="mt-4 flex flex-col gap-2 border-t border-border pt-3">
						{guidance.reviews.map((review) => (
							<li key={review.id} className="text-xs text-muted-foreground">
								<p>
									{labels[review.status]} por {review.reviewed_by},{" "}
									{djs(review.created_at).tz("America/Sao_Paulo").format("DD/MM/YYYY HH:mm")}
								</p>
								{review.review_note !== null && (
									<p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
										{review.review_note}
									</p>
								)}
							</li>
						))}
					</ol>
				)}
			</details>
			{guidance.status !== "rejected" && (
				<div className="mt-3 flex flex-wrap gap-2">
					{guidance.status === "proposed" && (
						<Button
							type="button"
							data-slot="guidance-approve"
							size="sm"
							onClick={() => setReviewing("approved")}
						>
							aprovar
						</Button>
					)}
					<Button
						type="button"
						size="sm"
						variant="outline"
						onClick={() => setReviewing("rejected")}
					>
						{rejectLabel}
					</Button>
				</div>
			)}
			{reviewing !== null && (
				<GuidanceReviewDialog
					guidance={guidance}
					status={reviewing}
					onClose={() => setReviewing(null)}
				/>
			)}
		</article>
	);
}
